import type { DefaultValues, FieldValues } from "react-hook-form"
import { FormProvider, useForm } from "react-hook-form"
import type { ReactElement, ReactNode, SubmitEvent } from "react"
import { useEffect, useRef } from "react"
import { toast } from "sonner"
import FormPageLeave from "./FormPageLeave"
import { handleError } from "./handleError"
import type { ActionCreatorResult, BaseQueryError } from "@/base"
import { useAutosave } from "@/base"

type Props<TValues extends FieldValues, Result> = {
  defaultValues: DefaultValues<TValues>
  resetKey?: string
  action: (values: TValues) => ActionCreatorResult<Result>
  successMessage?: string
  errorMessage?: string
  resetOnSuccess?: boolean
  onSuccess?: (result: Result) => Promise<void> | void
  pageLeave?: {
    autoSave: boolean
    confirmMessage?: string
  }
  children: ReactNode
  method?: "POST" | "GET"
  className?: string
}

// Overload: when onSuccess doesn't use the result, Result is unconstrained
export default function ApiFormWrapper<TValues extends FieldValues>(
  props: Omit<Props<TValues, unknown>, "onSuccess"> & {
    onSuccess?: () => Promise<void> | void
  },
): ReactElement

// Overload: when onSuccess uses the result, Result must match action
export default function ApiFormWrapper<TValues extends FieldValues, Result>(
  props: Props<TValues, Result>,
): ReactElement

export default function ApiFormWrapper<TValues extends FieldValues, Result>({
  defaultValues,
  resetKey,
  action,
  successMessage,
  errorMessage = "Something went wrong. Please try again.",
  resetOnSuccess = false,
  onSuccess,
  pageLeave,
  children,
  method = "POST",
  className,
}: Props<TValues, Result>) {
  const methods = useForm<TValues>({ defaultValues })
  const latestDefaultValuesRef = useRef(defaultValues)
  const {
    handleSubmit: makeHandleSubmit,
    reset,
    setError,
    clearErrors,
  } = methods

  // A save's cache upsert or navigation changes resetKey; skip that reset, or
  // it overwrites edits made meanwhile.
  const isSaveInFlightRef = useRef(false)

  useEffect(() => {
    latestDefaultValuesRef.current = defaultValues
  }, [defaultValues])
  useEffect(() => {
    if (isSaveInFlightRef.current) {
      isSaveInFlightRef.current = false

      return
    }
    methods.reset(latestDefaultValuesRef.current)
  }, [methods, resetKey])

  // `silent` (autosave) suppresses toasts and field errors.
  const saveForm = async (values: TValues, silent: boolean) => {
    isSaveInFlightRef.current = true

    // Only the request itself counts as a failed save: what follows runs after
    // the write, and reporting it would invite a retry that writes twice.
    let result: Result
    try {
      result = await action(values).unwrap()
    } catch (error) {
      isSaveInFlightRef.current = false
      if (!silent) {
        // Without field messages (a server error, no response) this toast is
        // the only sign the save failed.
        if (!handleError({ setError })(error as BaseQueryError)) {
          toast.error(errorMessage)
        }
      }

      throw error
    }

    if (resetOnSuccess && !silent) {
      reset()
    } else {
      // Update dirty baseline without changing field values – no focus loss
      methods.reset(methods.getValues(), { keepValues: true })
    }

    try {
      await onSuccess?.(result)
    } catch (error) {
      // Usually navigation. The save landed, so tell Sentry, not the user, and
      // leave isSaveInFlightRef armed: the save's cache echo is still coming.
      console.error("ApiFormWrapper: onSuccess failed after saving", error)
    }

    if (successMessage && !silent) {
      toast.success(successMessage)
    }
  }

  // Skips onSuccess, which often navigates: the user is already leaving.
  const onPageLeaveSave = async (values: TValues) => {
    isSaveInFlightRef.current = true
    try {
      await action(values).unwrap()
    } catch (error) {
      isSaveInFlightRef.current = false

      throw error
    }
  }

  const autoSave = !!pageLeave?.autoSave
  const { isDirty, isValid } = methods.formState

  const doAutoSave = async () => {
    await makeHandleSubmit((values: TValues) => saveForm(values, true))()
  }

  const { restart, cancel } = useAutosave(
    doAutoSave,
    autoSave && isDirty && isValid,
  )

  useEffect(() => {
    if (!autoSave) {
      return
    }
    // eslint-disable-next-line react-hooks/incompatible-library -- watch() subscription is the react-hook-form pattern for change detection; React Compiler skipping this component is acceptable
    const sub = methods.watch(() => {
      restart()
    })

    return () => {
      sub.unsubscribe()
    }
  }, [methods, autoSave, restart])

  const onFormSubmit = (event: SubmitEvent) => {
    event.preventDefault()
    event.stopPropagation()
    cancel()
    clearErrors()
    // saveForm rethrows for autosave and page-leave, which need to know the
    // save failed. Here the user has already been shown it.
    makeHandleSubmit((values: TValues) => saveForm(values, false))().catch(
      () => undefined,
    )
  }

  return (
    <FormProvider {...methods}>
      <form method={method} onSubmit={onFormSubmit} className={className}>
        {pageLeave && (
          <FormPageLeave<TValues>
            onSubmit={onPageLeaveSave}
            autoSave={pageLeave.autoSave}
            confirmMessage={pageLeave.confirmMessage}
          />
        )}
        {children}
      </form>
    </FormProvider>
  )
}
